import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { StorageNotConnected } from '@/components/documents/storage-not-connected';
import { ActivityIndicator, Text, useTheme } from '@/components/ui';
import {
	usePortfolioSearch,
	type FacetSelection,
	type PortfolioSearchItem,
	type SearchFacetValue,
} from '@/hooks/use-document-search';
import { formatDocumentSize } from '@/utils/document-download';
import { fileVisual } from '@/utils/document-folder-icons';

/**
 * Cross-association document search with facets (issue #404).
 *
 * ## Why the caveats are on screen
 *
 * Searching the drive live, with no index of our own, has limits the numbers don't show:
 * the provider's index lags an upload by minutes, facet counts describe the returned page
 * rather than the whole archive, and — depending on the Dropbox plan — a match may be on
 * FILE NAMES only rather than the text inside documents. Hiding those would make the
 * results look authoritative and quietly train people to distrust the feature the first
 * time they notice. The server states them; this renders them verbatim.
 */

interface Props {
	/**
	 * The ALREADY-DEBOUNCED query.
	 *
	 * The input lives in the page toolbar, not here: it has to sit beside the breadcrumb
	 * and stay mounted whether or not results are showing. Owning the field here would
	 * unmount it the moment the query cleared, which is exactly the focus-losing behaviour
	 * the stable-search rule forbids.
	 */
	query: string;
	onOpen: (item: PortfolioSearchItem) => void;
}

const FACET_LABELS: Record<keyof FacetSelection, string> = {
	association: 'Association',
	category: 'Category',
	year: 'Year',
};

export const DocumentSearch: React.FC<Props> = ({ query, onOpen }) => {
	const theme = useTheme();
	const [selection, setSelection] = useState<FacetSelection>({ association: [], category: [], year: [] });

	const debounced = query;
	const { data, isLoading, isFetching } = usePortfolioSearch(debounced, selection);

	const toggle = (dimension: keyof FacetSelection, value: string) => {
		setSelection((prev) => {
			const current = prev[dimension];
			return {
				...prev,
				[dimension]: current.includes(value)
					? current.filter((v) => v !== value)
					: [...current, value],
			};
		});
	};

	const activeCount = useMemo(
		() => selection.association.length + selection.category.length + selection.year.length,
		[selection],
	);

	const Facet: React.FC<{ dimension: keyof FacetSelection; values: SearchFacetValue[] }> = ({
		dimension, values,
	}) => {
		if (values.length === 0) return null;
		return (
			<View style={{ gap: 4 }}>
				<Text style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>
					{FACET_LABELS[dimension]}
				</Text>
				<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
					{values.slice(0, 12).map((facet) => {
						const active = selection[dimension].includes(facet.value);
						return (
							<Pressable
								key={facet.value}
								onPress={() => toggle(dimension, facet.value)}
								style={{
									flexDirection: 'row', alignItems: 'center', gap: 5,
									paddingHorizontal: 9, paddingVertical: 4, borderRadius: 13,
									backgroundColor: active ? (theme.colors.primary ?? '#1976d2') : '#eceff1',
								}}
							>
								<Text style={{ fontSize: 12, color: active ? '#fff' : '#37474f' }} numberOfLines={1}>
									{facet.value}
								</Text>
								<Text style={{ fontSize: 11, color: active ? '#e3f2fd' : '#78909c' }}>
									{facet.count}
								</Text>
							</Pressable>
						);
					})}
				</View>
			</View>
		);
	};

	const items = data?.items ?? [];

	return (
		<View style={{ gap: 10, padding: 12 }} testID="document-search">
			{/* The shared empty state, not a bespoke line: an admin who lands here gets the
			    same "Set up" affordance as every other document surface. */}
			{data?.unavailable === 'not-configured' && (
				<StorageNotConnected compact what="Search results" />
			)}

			{!!debounced && (isLoading || isFetching) && (
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
					{/* Inline, so the results below stay mounted rather than blanking. */}
					<ActivityIndicator size="small" />
					<Text style={{ fontSize: 12, color: '#888' }}>Searching…</Text>
				</View>
			)}

			{data && !data.unavailable && (
				<>
					<View style={{ gap: 8 }}>
						<Facet dimension="association" values={data.facets.association} />
						<Facet dimension="category" values={data.facets.category} />
						<Facet dimension="year" values={data.facets.year} />
					</View>

					<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
						{/* "N shown", never "of M" — `search_v2` reports no total, only whether
						    another page exists, so any figure past the page in hand would be
						    invented. */}
						<Text style={{ fontSize: 12, color: '#666' }} testID="document-search-count">
							{items.length} shown{data.hasMore ? ' · more available' : ''}
						</Text>
						{activeCount > 0 && (
							<Pressable onPress={() => setSelection({ association: [], category: [], year: [] })}>
								<Text style={{ fontSize: 12, color: theme.colors.primary }}>Clear filters</Text>
							</Pressable>
						)}
					</View>

					<ScrollView style={{ maxHeight: 420 }}>
						{items.map((item) => {
							const visual = fileVisual(item.extension);
							return (
								<Pressable
									key={item.id}
									onPress={() => onOpen(item)}
									style={{
										flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 7,
									}}
								>
									<Ionicons name={visual.icon} size={16} color={visual.color} />
									<View style={{ flex: 1, minWidth: 0 }}>
										<Text style={{ fontSize: 13 }} numberOfLines={1}>{item.name}</Text>
										{/* Falls back to the raw path. The drive holds documents outside
										    the association buckets (General/, the scan inbox) which have
										    no association or category — a hit with a blank subtitle reads
										    as a rendering bug rather than a file filed elsewhere. */}
										<Text style={{ fontSize: 11, color: '#90a4ae' }} numberOfLines={1}>
											{[item.association, item.category, item.year].filter(Boolean).join(' › ')
												|| item.path.join(' › ')
												|| 'Outside the association folders'}
										</Text>
									</View>
									<Text style={{ fontSize: 11, color: '#999' }}>{formatDocumentSize(item.size)}</Text>
								</Pressable>
							);
						})}
						{debounced && items.length === 0 && !isFetching && (
							<Text style={{ fontSize: 13, color: '#666', paddingVertical: 8 }}>
								Nothing matched.
							</Text>
						)}
					</ScrollView>

					{/* Stated, not hidden — see the module header. */}
					{data.caveats.map((caveat) => (
						<Text key={caveat} style={{ fontSize: 11, color: '#9e9e9e' }}>{caveat}</Text>
					))}
				</>
			)}
		</View>
	);
};
